import React, { useEffect, useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import GetAppIcon from '@mui/icons-material/GetApp';
import Typography from '@mui/material/Typography';

import EnhancedTable from 'components/EnhancedTable';
import EnhancedTableEx from 'components/EnhancedTableEx';
import ContextStore from 'modules/context';
import SimpleTableToolbar from 'components/SimpleTableToolbar';
import CompanyFilter from 'components/CompanyFilter';
import LeaveType from 'enum/LeaveType';
import { firestoreListener } from 'modules/firebase';

function LeaveOvertimeDetail({ salary: { id: yearMonth, salaryData, source }, userMapping, userRights, currentCompany, currentUser, vendorMapping }) {
  const { formatMessage } = useIntl()
  const tableRef = useRef()
  const startDate = dayjs(yearMonth).format('YYYY-MM') + '-01';
  const endDate = dayjs(yearMonth).format('YYYY-MM') + `${dayjs(yearMonth).daysInMonth()}`;
  const [worktimePolicyMapping, setWorktimePolicyMapping] = useState({})
  const [leaveBalance, setLeaveBalance] = useState([])
  const [shifts, setShifts] = useState([])
  const [leaves, setLeaves] = useState([])
  const [overtimes, setOvertimes] = useState([])
  const [userSalaryMapping, setUserSalaryMapping] = useState({})
  const currentData = Object.keys(salaryData).reduce((acc, cur) => {
    acc.push({ id: cur, ...salaryData[cur] })
    return acc
  }, []).filter(c => {
    if (!userRights.hasUserRightForVendor('finance-salary', currentCompany)) {
      return c.id === currentUser.key
    } else {
      return c
    }
  }).sort((a, b) => {
    const e = userMapping[a.id].employeeId.replace(/[^0-9]/ig, '')
    const e2 = userMapping[b.id].employeeId.replace(/[^0-9]/ig, '')

    if (e < e2) {
      return -1
    } else if (e > e2) {
      return 1
    } else {
      return 0
    }
  })
  
  useEffect(() => {
    const unsubscribe = firestoreListener({
      collection: 'userSalarys',
      mapping: true,
      onData: (data) => {
        setUserSalaryMapping(data)
      }
    })
  
    return () => unsubscribe()
  }, []);

  useEffect(() => {
    const unsubscribe = firestoreListener({
      collection: 'leaveBalances',
      mapping: true,
      onData: (data) => {
        setLeaveBalance(data)
      }
    })
  
    return () => unsubscribe()
  }, []);

  useEffect(() => {
    const unsubscribe = firestoreListener({
      collection: 'worktimePolicies',
      mapping: true,
      onData: (data) => {
        setWorktimePolicyMapping(data)
      }
    })

    return () => unsubscribe()
  }, []);

  useEffect(() => {
    const unsubscribe = firestoreListener({
      collection: 'shifts',
      where: [
        ['date', '>=', startDate],
        ['date', '<=', endDate],
      ],
      onData: (data) => {
        setShifts(data)
      }
    })

    return () => unsubscribe()
  }, [startDate, endDate]);

  useEffect(() => {
    const unsubscribe = firestoreListener({
      collection: 'leaves',
      where: [
        ['status', '==', 'done'],
        ['startDate', '>=', startDate],
        ['startDate', '<=', endDate],
      ],
      onData: (data) => {
        setLeaves(data)
      }
    })

    return () => unsubscribe()
  }, [startDate, endDate]);


  useEffect(() => {
    const unsubscribe = firestoreListener({
      collection: 'overtimes',
      where: [
        ['status', '==', 'done'],
        ['startDate', '>=', startDate],
        ['startDate', '<=', endDate],
      ],
      onData: (data) => {
        setOvertimes(data)
      }
    })

    return () => unsubscribe()
  }, [startDate, endDate]);

  const formatData = (data) => {
    const newData = { ...data }
    newData.name = userMapping[newData.id].displayName
    newData.employeeId = userMapping[newData.id].employeeId

    let partTime = userSalaryMapping[newData.id] ? userSalaryMapping[newData.id].partTime : false

    const wk = userMapping[newData.id].worktime
    let leaveCount = 0
    
    for (const l of LeaveType) {
      const leave = leaves.filter(i => i.createdBy === newData.id && i.type === l.value).reduce((acc, cur) => {
        acc += (cur.days * wk) + cur.minutes / 60
        return acc
      }, 0)

      
      newData[l.value] = partTime ? 'N/A' : `${((leave - (leave % wk))) / wk}d${leave % wk}h`
      if(l.value !== 'maternity_leave') {
        leaveCount += leave
      }     
    }

    let overtimeCount_Pay = { normal: 0, special: 0 }
    let overtimeCount_Leave = { normal: 0, special: 0 }

    let overtimeCount = 0
    overtimes.filter(i => i.createdBy === newData.id).forEach(overtime => {
      if(overtime.type === 'overtime_leave') {
        if ((overtime.minutes / 60) <= 2) {
          overtimeCount_Leave.normal += overtime.minutes / 60
        } else {
          overtimeCount_Leave.normal += 2
          overtimeCount_Leave.special += ((overtime.minutes / 60) - 2)
        }
      } else {
        if ((overtime.minutes / 60) <= 2) {
          overtimeCount_Pay.normal += overtime.minutes / 60
        } else {
          overtimeCount_Pay.normal += 2
          overtimeCount_Pay.special += ((overtime.minutes / 60) - 2)
        }
      }

      overtimeCount += overtime.minutes / 60
    })

    newData.month_leave = `${overtimeCount_Leave.normal + overtimeCount_Leave.special}h`
    newData.month_overtime_leave = `${leaves.filter(i => i.createdBy === newData.id && i.type === 'overtime_leave').reduce((acc, cur) => {
      acc += (cur.days * wk) + (cur.minutes / 60)
      return acc
    }, 0)}h`

    newData.month_overtimeTotal = `${Number(newData.month_leave.replace('h', '')) - Number(newData.month_overtime_leave.replace('h', ''))}h`
    newData.overtimeTotal = `${leaveBalance[newData.id]?.overtime_leave.balance / 60}h`
    newData.overtime_leave = `${overtimeCount_Leave.normal}h/${overtimeCount_Leave.special}h`
    newData.overtime_pay = `${overtimeCount_Pay.normal}h/${overtimeCount_Pay.special}h`

    // 計算出勤時數	-----------------

    const newLateCount = newData.lateCount.split(/[h m]/)
  
    if (userMapping[newData.id].worktimeType === 'shift') {
      const currentShift =shifts.filter(s => s.staff === newData.id).reduce((acc, cur) => {
        const diffM = dayjs(`${cur.date} ${cur.endTime}`).diff(dayjs(`${cur.date} ${cur.startTime}`), 'minute')
        // if(diffM > wk * 60) {
        //   acc += wk * 60
        // } else {
        acc += diffM
        // }
        return acc
      }, 0)

      const newShifts = Math.round(currentShift - (leaveCount * 60) - ((Number(newLateCount[0]) * 60) +  Number(newLateCount[1])) - ((newData.absenteeismCount * wk) * 60) + (overtimeCount * 60))
      newData.attendanceHours = `${((newShifts - (newShifts % 60)) / 60)}h${(newShifts % 60)}m`

      // if(newData.id === 'TezOZosVytaZdTLADi6XYewgnJv1') {
      //   console.log(newShifts, `${((newShifts - (newShifts % 60)) / 60)}h${(newShifts % 60)}m`)
      // }
    } else {
      const worktime = worktimePolicyMapping[userMapping[newData.id].worktimePolicies]?.worktime ?? {}

      let newTime = 0

      for (let i = 0; i < dayjs(yearMonth).daysInMonth(); i++) {
        const str = (i + 1).toString().padStart(2, '0')
        newTime += Object.keys(worktime).reduce((acc, cur) => {
          if (cur === dayjs(`${yearMonth}-${str}`).format('ddd')) {
            const diffM = dayjs(`${yearMonth}-${str} ${worktime[cur].endTime}`).diff(dayjs(`${yearMonth}-${str} ${worktime[cur].startTime}`), 'minute')
            acc += diffM
          }
          return acc
        }, 0)
      }

      newTime = Math.round(newTime - (leaveCount * 60) - ((Number(newLateCount[0]) * 60) +  Number(newLateCount[1])) - ((newData.absenteeismCount * wk) * 60) + (overtimeCount * 60))
      newData.attendanceHours = `${((newTime - (newTime % 60)) / 60)}h${(newTime % 60)}m`
    }

    // 排班時間 - 休假時數 - 遲到早退 - 曠職天數 + 加班時數 = 出勤時數
    // -------------------
    return newData
  }

  const _headerCells = [
    { text: 'employeeId', order: 0 },
    { text: 'name', order: 1 },
    { text: 'attendanceHours', order: 2 },

    { text: 'lateCount', order: 90 },
    { text: 'absenteeismCount', order: 91 },
    { text: 'reviseCount', order: 92 },

    { text: 'overtime_pay', order: 93 },
    { text: 'overtime_leave', order: 94 },
    { text: 'month_leave', order: 95 },
    { text: 'month_overtime_leave', order: 96 },
    { text: 'month_overtimeTotal', order: 97 },
    { text: 'overtimeTotal', order: 98 },
  ].map(c => { c.text = formatMessage({ id: `finance.leaveOvertime.detail.${c.text}` }); return c })

  const _rowCells = [
    { field: 'employeeId', order: 0 },
    { field: 'name', order: 1 },
    { field: 'attendanceHours', order: 2 },

    { field: 'lateCount', order: 90 },
    { field: 'absenteeismCount', order: 91 },
    { field: 'reviseCount', order: 92 },

    { field: 'overtime_pay', order: 93 },
    { field: 'overtime_leave', order: 94 },
    { field: 'month_leave', order: 95 },
    { field: 'month_overtime_leave', order: 96 },
    { field: 'month_overtimeTotal', order: 97 },
    { field: 'overtimeTotal', order: 98 },
  ]

  let cellOrder = 3
  for (const l of LeaveType) {    
    if (!['overtime_leave', 'weekly_leave'].includes(l.value)) {
      _headerCells.push({ text: l.name, order: cellOrder })
      _rowCells.push({ field: l.value, order: cellOrder })

      cellOrder++
    }
  }

  const headerCells = _headerCells.sort((a, b) => a.order - b.order)
  const rowCells = _rowCells.sort((a, b) => a.order - b.order)


  function exportSalary() {
    tableRef.current.saveToFile(`LeaveOvertime${yearMonth}_${vendorMapping[source].name}.csv`)
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={12} md={12} sx={{ mt: '10px' }}>
        <EnhancedTableEx
          ref={tableRef}
          headerCells={headerCells}
          rowCells={rowCells}
          tableData={currentData.map(c => formatData(c))}
          getHeaderActionIcons={() =>
            <IconButton
              onClick={() => exportSalary()}
              size="large">
              <GetAppIcon></GetAppIcon>
              <Typography noWrap variant="button">{formatMessage({ id: 'button.export' })}</Typography>
            </IconButton>
          }
          getExpandContent={() => null}
        />
      </Grid>
    </Grid>
  );
}

LeaveOvertimeDetail.propTypes = {
  salary: PropTypes.object.isRequired,
  userMapping: PropTypes.object.isRequired,
};

function LeaveOvertimePage() {
  const { formatMessage } = useIntl()
  const vendorMapping = useSelector(state => state.vendors.data);
  const { currentCompany, currentUser } = useContext(ContextStore)
  const userRights = useSelector(state => state.userRights);
  const [salaryList, setSalaryList] = useState([])
  const userMapping = useSelector(state => state.users.data);

  useEffect(() => {
    const unsubscribe = firestoreListener({
      collection: 'salaryList',
      where: [['source', '==', currentCompany]],
      onData: (data) => {
        setSalaryList(data)
      }
    })

    return () => unsubscribe()
  }, [currentCompany]);

  const formatData = (data) => {
    const newData = { ...data }
    newData.yearMonth = newData.id
    newData.createdAt = dayjs(newData.createdAt.seconds * 1000).format('YYYY-MM-DD')
    newData.createdBy = userMapping[newData.createdBy].displayName
    return newData
  }

  const headerCells = [
    { text: 'yearMonth', sort: 'yearMonth' },
    { text: 'createdBy', sort: 'createdBy' },
    { text: 'createdAt', sort: 'createdAt' },
  ].map(c => { c.text = formatMessage({ id: `finance.salary.header.${c.text}` }); return c })

  const rowCells = [
    { field: 'yearMonth' },
    { field: 'createdBy' },
    { field: 'createdAt' },
  ]

  return (
    <Grid container spacing={1} sx={{ mt: '10px' }}>
      <Grid item xs={12} sm={12} md={12} sx={{ mt: '4px' }}>
        <SimpleTableToolbar
          title='finance.leaveOvertime.title'
          toolbox={<Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={9} />
            <Grid item xs={12} sm={12} md={3}>
              <CompanyFilter userRight='finance-salary' />
            </Grid>
          </Grid>}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <EnhancedTable
          defaultOrder="asc"
          defaultOrderField="date"
          headerCells={headerCells}
          rowCells={rowCells}
          tableData={salaryList.map(s => formatData(s))}
          getExpandContent={salary => <LeaveOvertimeDetail
            salary={salary}
            userMapping={userMapping}
            currentCompany={currentCompany}
            userRights={userRights}
            currentUser={currentUser}
            vendorMapping={vendorMapping}
          />}
        />
      </Grid>
    </Grid>
  );
}

export default LeaveOvertimePage;
